import { graphql } from 'gatsby'
import React, { useEffect, useState } from 'react'

import useMediaQuery from '../../../hooks/useMediaQuery'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { useHeaderStyle, useHeaderStyleBase } from '../../../lib/header/style'
import { dispensaryImageUrlFor } from '../../../lib/sanity'
import { dispensaryLogoCdn$ } from '../../../lib/sanity-dispensaries/query'
import { cn } from '../../utils/cn'
import Cart from '../cart/Cart'
import { BrandMenu } from './BrandMenu'
import { DesktopHeader } from './DesktopHeader'
import { MobileHeader } from './MobileHeader'

import printLogoImage from '../../../img/logo-dark.svg'

export interface HeaderProps {
  className?: string
}

export const Header: React.FC<HeaderProps> = ({ className }) => {
  const isMobile = useMediaQuery('(max-width: 767px)')
  const isNarrow = useMediaQuery('(max-width: 1023px)')
  const threshold = isNarrow ? 74 : 101
  const dispensary = useDispensaryContext()
  const [dispensaryLogoSrc, setDispensaryLogoSrc] = useState<string | null>(null)
  const headerStyleBase = useHeaderStyleBase()
  const {
    classes: { backgroundColor, textColor },
  } = useHeaderStyle()

  useEffect(() => {
    if (!dispensary) {
      return
    }
    // create subscription instead (of useDispensaryLogoQuery()) since we only want a
    // subscription on dispensaries
    const subscription = dispensaryLogoCdn$.subscribe((value) => {
      setDispensaryLogoSrc(
        value._tag === 'Right' && value.data?.logoImageUrl
          ? dispensaryImageUrlFor(value.data.logoImageUrl).height(90).dpr(3).auto('format').url()
          : null,
      )
    })
    return () => subscription.unsubscribe()
  }, [dispensary])

  return (
    <>
      <div
        className={cn(
          'w-full',
          headerStyleBase === 'transparent' ? 'pt-[48px]' : 'pt-[82px] md:pt-[126px] lg:pt-[142px]',
          backgroundColor,
          textColor,
        )}
      />

      <header
        className={cn(
          'fixed left-0 top-0 h-0 w-full z-40 transition-all duration-500 box-border print:static',
          className,
        )}
      >
        <div className="relative print:hidden h-0">
          <BrandMenu />
          {isMobile ? <MobileHeader /> : <DesktopHeader threshold={threshold} />}
        </div>
        <Cart />
        <img
          alt="Logo"
          className="hidden print:block print:h-[50px] print:m-[10px_auto]"
          src={dispensaryLogoSrc || printLogoImage}
        />
      </header>
    </>
  )
}

export const utilityMenu = graphql`
  fragment utilityMenuFragment on SanitySiteSettings {
    utilityMenu {
      ...MenuEntryFragment
    }
  }
`

export const brandMenu = graphql`
  fragment brandMenuFragment on SanitySiteSettings {
    brandMenu {
      ...MenuEntryFragment
    }
  }
`
